import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import {
  CdkConnectedOverlay,
  ConnectedPosition,
  OverlayModule,
  ScrollStrategy,
  createCloseScrollStrategy,
} from '@angular/cdk/overlay';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

let idCounter = 0;

/**
 * Unter dem Feld, bei zu wenig Platz darueber. Das Umschlagen uebernimmt die
 * Positionsstrategie des Overlays.
 */
const PANEL_POSITIONS: ConnectedPosition[] = [
  {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4},
  {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4},
];
const PANEL_MIN_HEIGHT_REM = 10;

@Component({
  selector: 'app-ci-select',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './ci-select.component.html',
  styles: [':host { display: block; width: 100%; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CiSelectComponent),
      multi: true,
    },
  ],
})
export class CiSelectComponent<T> implements ControlValueAccessor, OnDestroy {
  private readonly _items = signal<readonly T[]>([]);

  @Input({ required: true })
  set items(value: readonly T[]) {
    this._items.set(value ?? []);
  }
  get items(): readonly T[] {
    return this._items();
  }

  @Input() variant: 'outline' | 'filled' = 'filled';
  /**
   * Panel schrumpft bei wenig Platz, statt per `push` ueber das Feld zu rutschen (Touch-Fix
   * NP20-837). Vorerst nur fuer die Sprachfelder; aus ist das Verhalten wie zuvor.
   */
  @Input() shrinkPanelToFit = false;
  @Input() label = '';
  @Input() required = false;
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() searchClearLabel = '';
  @Input() ariaLabel = '';
  @Input() emptyLabel = '';
  @Input() errorText = '';
  @Input() hitTemplate = '';
  @Input() displayWith: (item: T) => string = (item) => String(item ?? '');
  @Input() compareWith: (a: T, b: T) => boolean = (a, b) => a === b;
  @Input() isDisabled: (item: T) => boolean = () => false;

  @Input() set invalid(value: boolean) {
    this._invalid.set(!!value);
  }
  @Input() set disabled(value: boolean) {
    this._disabled.set(!!value);
  }

  @Output() valueChange = new EventEmitter<T | null>();
  @Output() openChange = new EventEmitter<boolean>();

  @ViewChild('field') fieldRef?: ElementRef<HTMLElement>;
  @ViewChild(CdkConnectedOverlay) private overlay?: CdkConnectedOverlay;

  protected readonly id = `ci-select-${++idCounter}`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly open = signal(false);
  protected readonly panelWidth = signal(0);
  // Ohne minHeight schrumpft CDK nie und schiebt ein zu hohes Panel per `push` ueber das Feld;
  // beim Antippen landet der click dann ausserhalb und schliesst (NP20-837). Pixel, weil CDK
  // nur px fuer die Passprobe auswertet.
  protected readonly panelMinHeight = signal<number | undefined>(undefined);
  protected readonly panelPositions = PANEL_POSITIONS;
  // Schliessen statt Nachfuehren: beim Nachfuehren stauchte ein nach oben geoeffnetes Panel
  // und legte sich ueber das Feld. Ohne Optionen — ein threshold > 1 nimmt in
  // CloseScrollStrategy den Zweig, der nachfuehrt statt zu schliessen.
  protected readonly scrollStrategy: ScrollStrategy;
  protected readonly searchTerm = signal('');
  protected readonly focusedIndex = signal(-1);
  protected readonly value = signal<T | null>(null);
  protected readonly _invalid = signal(false);
  protected readonly _disabled = signal(false);

  private readonly normalizedTerm = computed(() =>
    this.normalize(this.searchTerm().trim()),
  );

  protected readonly filtered = computed(() => {
    const term = this.normalizedTerm();
    const items = this._items();
    if (!term) return [...items];
    return items.filter((i) =>
      this.normalize(this.displayWith(i)).includes(term),
    );
  });

  // Ungefiltert: gewaehlter Eintrag steht oben (mit Divider), dann Rest in
  // fachlicher Sortierung. Gefiltert: allein die Trefferreihenfolge.
  protected readonly displayItems = computed<
    { item: T; kind: 'selected' | 'regular' }[]
  >(() => {
    const term = this.normalizedTerm();
    const selected = this.value();
    if (term || selected === null) {
      return this.filtered().map((item) => ({
        item,
        kind: 'regular' as const,
      }));
    }
    const rest = this._items().filter((i) => !this.compareWith(i, selected));
    return [
      { item: selected, kind: 'selected' as const },
      ...rest.map((item) => ({ item, kind: 'regular' as const })),
    ];
  });

  // Einmal pro Auswahl/Suchbegriff aufgebaut. Frueher rief das Template
  // `highlight(item)` direkt auf: `bypassSecurityTrustHtml` liefert bei jedem
  // Aufruf ein neues Objekt, weshalb Angular das innerHTML jeder Option in
  // jedem Zyklus neu schrieb — bei 197 Eintraegen inklusive NFD-Durchlauf.
  protected readonly renderItems = computed(() =>
    this.displayItems().map((entry) => ({
      item: entry.item,
      kind: entry.kind,
      html: this.buildHighlight(entry.item),
    })),
  );

  protected hitsText(): string {
    return this.hitTemplate
      .replace('{shown}', String(this.filtered().length))
      .replace('{total}', String(this._items().length));
  }

  /**
   * `_items()` muss gelesen werden: erst diese Abhaengigkeit laesst das geschlossene Feld einen
   * Neuaufbau der Liste (Sprachwechsel, Umbenennen) unter OnPush ueberhaupt bemerken.
   */
  protected readonly displayValue = computed(() => {
    const v = this.value();
    if (v === null || v === undefined) return '';
    const current = this._items().find((i) => this.compareWith(i, v));
    return this.displayWith(current ?? v);
  });

  protected optionId(index: number): string {
    return `${this.id}-opt-${index}`;
  }

  protected isSelected(item: T): boolean {
    const v = this.value();
    return v !== null && v !== undefined && this.compareWith(v, item);
  }

  protected onFieldClick(): void {
    if (this._disabled()) return;
    if (this.open()) {
      this.setOpen(false);
      this.onTouched();
      return;
    }
    this.openPanel();
  }

  private openPanel(): void {
    this.syncPanelWidth();
    this.setOpen(true);
  }

  private syncPanelWidth(): void {
    this.panelWidth.set(this.fieldRef?.nativeElement.offsetWidth ?? 0);
    if (!this.shrinkPanelToFit) {
      this.panelMinHeight.set(undefined);
      return;
    }
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    this.panelMinHeight.set(PANEL_MIN_HEIGHT_REM * rem);
  }

  /**
   * Das Panel haengt am Feld, nicht im Seitenfluss: waechst das Feld, muss die Position
   * nachgezogen werden. Die Positionsstrategie tut das von sich aus nur bei Viewport-Aenderung.
   */
  protected onFieldGeometryChange(): void {
    this.syncPanelWidth();
    const ref = this.overlay?.overlayRef;
    if (ref?.hasAttached()) ref.updatePosition();
  }

  /** Nur solange offen, damit nicht jedes Feld der Seite an jedem Ereignis haengt. */
  private trackWhileOpen(track: boolean): void {
    if (track === !!this.stopTracking) return;
    if (!track) {
      this.stopTracking?.();
      this.stopTracking = undefined;
      return;
    }
    const cleanups: (() => void)[] = [];

    const field = this.fieldRef?.nativeElement;
    if (field && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.onFieldGeometryChange());
      observer.observe(field);
      cleanups.push(() => observer.disconnect());
    }

    // overlayOutsideClick haengt an `click`. Ein Druck auf die Scrollbar erzeugt keinen —
    // vor der Overlay-Umstellung schloss das Panel per mousedown, das bleibt so.
    const onMouseDown = (event: MouseEvent) => this.onDocumentMouseDown(event);
    document.addEventListener('mousedown', onMouseDown);
    cleanups.push(() => document.removeEventListener('mousedown', onMouseDown));

    this.stopTracking = () => cleanups.forEach((fn) => fn());
  }

  private onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (this.host.nativeElement.contains(target)) return;
    if (this.overlayElement()?.contains(target)) return;
    this.setOpen(false);
    this.onTouched();
  }

  private overlayElement(): HTMLElement | null {
    return this.overlay?.overlayRef?.overlayElement ?? null;
  }

  /** Das Suchfeld liegt im Overlay, nicht im Host — der Zugriff laeuft ueber das Panel. */
  private panelInput(): HTMLInputElement | null {
    return this.overlayElement()?.querySelector<HTMLInputElement>('.ci-select__search') ?? null;
  }

  protected onOverlayAttached(): void {
    this.panelInput()?.focus();
  }

  /**
   * Ein Detach ohne vorheriges Schliessen kommt von der Scroll-Strategie, nicht vom Nutzer —
   * darum der emissionsfreie Pfad und kein `onTouched()`.
   */
  protected onDetach(): void {
    this.applyOpen(false);
  }

  protected onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) return;
    this.setOpen(false);
    this.onTouched();
  }

  /**
   * Der Fokus steht beim Schliessen im Suchfeld des Panels, das gleich aus dem DOM
   * fliegt. Ohne Rueckgabe an das Feld landet er auf `body` und Tab beginnt vorn.
   */
  private focusField(): void {
    queueMicrotask(() => this.fieldRef?.nativeElement.focus());
  }

  protected onSearchInput(term: string): void {
    this.searchTerm.set(term ?? '');
    const list = this.displayItems();
    this.focusedIndex.set(list.findIndex((entry) => !this.isDisabled(entry.item)));
  }

  private scrollFocusedIntoView(): void {
    const index = this.focusedIndex();
    if (index < 0) return;
    const option = this.overlayElement()?.querySelector<HTMLElement>(`[id="${this.optionId(index)}"]`);
    option?.scrollIntoView?.({ block: 'nearest' });
  }

  protected clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchTerm.set('');
    this.focusedIndex.set(-1);
    queueMicrotask(() => this.panelInput()?.focus());
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this._disabled()) return;
    const items = this.displayItems();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
          return;
        }
        this.focusedIndex.update((i) => {
          if (!items.length) return -1;
          let next = (i + 1) % items.length;
          const start = next;
          while (this.isDisabled(items[next].item)) {
            next = (next + 1) % items.length;
            if (next === start) break;
          }
          return next;
        });
        this.scrollFocusedIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
          return;
        }
        this.focusedIndex.update((i) => {
          if (!items.length) return -1;
          let prev = (i - 1 + items.length) % items.length;
          const start = prev;
          while (this.isDisabled(items[prev].item)) {
            prev = (prev - 1 + items.length) % items.length;
            if (prev === start) break;
          }
          return prev;
        });
        this.scrollFocusedIntoView();
        break;
      case 'Enter':
        if (!this.open()) {
          event.preventDefault();
          this.openPanel();
          break;
        }
        if (this.focusedIndex() >= 0) {
          event.preventDefault();
          const entry = items[this.focusedIndex()];
          if (entry) this.select(entry.item);
        }
        break;
      // Oeffnet nur das geschlossene Feld — im Panel gehoert das Leerzeichen in die Suche.
      case ' ':
        if (!this.open() && !(event.target as HTMLElement | null)?.closest('input')) {
          event.preventDefault();
          this.openPanel();
        }
        break;
      case 'Escape':
        if (this.open()) {
          event.preventDefault();
          this.setOpen(false);
          this.focusField();
        }
        this.onTouched();
        break;
      case 'Tab':
        // Das fokussierte Suchfeld liegt im Overlay und fliegt gleich aus dem DOM; ohne
        // Rueckgabe landet der Fokus auf `body` und Tab beginnt vorn.
        if (this.open()) this.focusField();
        this.setOpen(false);
        this.onTouched();
        break;
    }
  }

  protected select(item: T): void {
    if (this._disabled() || this.isDisabled(item)) return;
    this.applyValue(item);
    this.setOpen(false);
    this.focusField();
  }

  private applyValue(next: T | null): void {
    this.value.set(next);
    this.onChange(next);
    this.onTouched();
    this.valueChange.emit(next);
  }

  /**
   * Einziger Schreibpfad auf `open` mit Emission. `openChange` faellt hier statt in einem
   * Effect: eine Emission mitten in der Change Detection laesst den Handler des Aufrufers
   * waehrend des Zyklus schreiben — NG0100 im Dev-Modus, ein zusaetzlicher Durchlauf
   * in Prod.
   */
  private setOpen(next: boolean): void {
    if (this.open() === next) return;
    this.applyOpen(next);
    this.openChange.emit(next);
  }

  private applyOpen(next: boolean): void {
    this.open.set(next);
    this.trackWhileOpen(next);
    if (!next) {
      this.searchTerm.set('');
      this.focusedIndex.set(-1);
    }
  }

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly sanitizer: DomSanitizer,
    injector: Injector,
  ) {
    this.scrollStrategy = createCloseScrollStrategy(injector);
  }

  /** Ohne Emission: die Zerstoerung faellt mitten in die Change Detection des Aufrufers. */
  ngOnDestroy(): void {
    this.applyOpen(false);
  }

  writeValue(v: T | null): void {
    this.value.set(v === undefined ? null : v);
  }
  registerOnChange(fn: (v: T | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(d: boolean): void {
    this._disabled.set(d);
  }

  private stopTracking?: () => void;
  private onChange: (v: T | null) => void = () => {};
  private onTouched: () => void = () => {};

  private normalize(s: string): string {
    // NFD + Entfernen der Combining-Diacritical-Marks (U+0300..U+036F).
    return s
      .normalize('NFD')
      // eslint-disable-next-line no-misleading-character-class
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildHighlight(item: T): SafeHtml {
    const label = this.displayWith(item);
    const term = this.searchTerm().trim();
    if (!term) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(label));
    }

    // Char-fuer-Char normalisieren und Startpositionen im Original merken —
    // so bleibt die Trefferposition trotz Diakritika-Entfernung korrekt.
    const chars = [...label];
    let normalized = '';
    const startPositions: number[] = [];
    for (let i = 0; i < chars.length; i++) {
      const norm = this.normalize(chars[i]);
      for (let k = 0; k < norm.length; k++) {
        startPositions.push(i);
      }
      normalized += norm;
    }
    const nTerm = this.normalize(term);
    if (!nTerm) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(label));
    }
    const idx = normalized.indexOf(nTerm);
    if (idx < 0) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(label));
    }
    const startChar = startPositions[idx];
    const endChar = startPositions[idx + nTerm.length - 1] + 1;

    const before = this.escapeHtml(chars.slice(0, startChar).join(''));
    const match = this.escapeHtml(chars.slice(startChar, endChar).join(''));
    const after = this.escapeHtml(chars.slice(endChar).join(''));
    return this.sanitizer.bypassSecurityTrustHtml(
      `${before}<strong>${match}</strong>${after}`,
    );
  }
}